import React from 'react';
import { Link, mergeStyles, Persona, PersonaSize } from '@fluentui/react';
import { ITextSlots, Text } from '@fluentui/react/lib/Text';
import { Check } from '@fluentui/react';
import { ViewTileImageRenderer } from './ViewTileImageRenderer';
import { ITileDisplayField } from '../../../../interfaces/IViewsDropdown';
import { peoplePill, peoplePillPersonaStyles, pillContainer, pillStyle } from '../../../../../../contentManager/Body/useContentManagerBody';
import { recentFilesAdvancedSearchMapper } from '../../../mappers/recentFilesAdvancedSearchMapper';
import { rowLockIndicator, sharePointInternalFileName, sharePointInternalTitleName } from '../../../../config/constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/storeMain';
import { IMetaTextColor } from '../../../../interfaces/ITenantInfo';
import { ColorPair, generateColorForValue } from '../../../../../../utilities/DisplayValueFormatUtil';
import { IExternalResourceUrl } from '../../../../../../contentManager/Body/ContentBodyFilesFolders';
import { getGridItemUrl, openInHostDocubird, tryGetUrl } from '../../../helpers/UrlHelper';
import { LanguageContext } from '../../../../../../utilities/LocalizationModule';
import { resolveShortcutOpenUrl } from '../../../helpers/shortcutOpen';
import { ImageRenderer } from '../ImageRenderer/ImageRenderer';

const imageStyles = mergeStyles({
    objectFit: "contain",
    marginBottom: 7,
    height: 165,
    maxWidth: 255,
    borderRadius: 3
});

const tileStyles = mergeStyles({
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    minWidth: 200,
    width: 255,
    cursor: "pointer",
    borderRadius: 3,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'scale(1.01)',
        boxShadow: '0 1.6px 3.6px 0 rgba(0, 0, 0, .133), 0 .3px .9px 0 rgba(0, 0, 0, .11)'
    },
    [`&:hover > .${imageStyles}`]: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
    },
    '&.is-selected': {
        outline: `1px solid var(--db-accent, #0078d4)`,
        transform: 'scale(1.01)',
    },
    '&.no-image': {
        paddingTop: 10,
    },
    '&.detailed': {
        boxShadow: '0 1.6px 3.6px 0 rgba(0, 0, 0, .133), 0 .3px .9px 0 rgba(0, 0, 0, .11)',
        alignItems: "unset",
        width: 255
    },
    [`&.detailed > .${imageStyles}`]: {
        objectFit: "cover",
        height: 200,
        width: 255
    },
});

const disabledTileStyles: React.CSSProperties = {
    pointerEvents: "none",
    cursor: "not-allowed",
    opacity: 0.5
}

const tileCheckStyles = mergeStyles({
    position: "absolute",
    top: 5,
    right: 5,
    display: "none",
    [`.${tileStyles}:hover &`]: {
        display: "inline-flex"
    },
    [`.${tileStyles}.is-selected &`]: {
        display: "inline-flex"
    }
});

const textTitleStyles: ITextSlots = {
    root: { color: 'var(--db-fg-muted, #605e5c)' }
}

const textStyles: React.CSSProperties = {
    display: "inline-block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginLeft: 15,
    marginBottom: 5,
    maxWidth: 240
}

const textStylesAlt: React.CSSProperties = {
    ...textStyles,
    marginBottom: 15
}

const textStylesNoSidePad: React.CSSProperties = {
    ...textStyles,
    marginLeft: 0,
    marginRight: 0
}

interface IPeoplePillItem {
    id: string;
    value?: string;
    title?: string;
}

const DisplayText = React.memo(({ text, bold = false }: { text: string | null | undefined, bold?: boolean }) => {
    const displayText = text || "-";
    const title = text && text.length > 38 ? text : undefined;
    const style = bold ? { fontWeight: 600 } : undefined;

    return <span title={title} style={style}>{displayText}</span>;
});

const ColouredDisplayText = React.memo(({ displayField, text, metadataTextColors }: { displayField: ITileDisplayField, text: string | null | undefined, metadataTextColors: IMetaTextColor[] | null | undefined }) => {
    if (!text) return <>-</>;

    const valueColor: ColorPair = generateColorForValue(text, metadataTextColors, displayField.type === "Text");
    if (!valueColor.backgroundColor && !valueColor.textColor) {
        const title = text && text.length > 38 ? text : undefined;
        return <span title={title}>{text}</span>;
    }

    return <div style={pillContainer}>
        <div style={{ ...pillStyle, backgroundColor: valueColor.backgroundColor }}>
            <span style={{ color: valueColor.textColor, overflow: "hidden", textOverflow: "ellipsis" }}>{text}</span>
        </div>
    </div>;
});

const PeoplePills = React.memo(({ items }: { items: IPeoplePillItem[] | undefined }) => {
    if (!Array.isArray(items) || items.length === 0) {
        return <>-</>;
    }

    return (
        <div style={pillContainer}>
            {items.map(item => (
                <div key={item.id} style={peoplePill}>
                    <Persona
                        styles={peoplePillPersonaStyles}
                        text={item.value || item.title}
                        size={PersonaSize.size24}
                        showOverflowTooltip={false}
                    />
                </div>
            ))}
        </div>
    );
});

const onUrlClick = async (item: any) => {
    const url = await resolveShortcutOpenUrl(item);
    if (url) {
        window.open(url, "_blank");
    }
}

interface TileProps {
    isSharePointList: boolean;
    viewItem: any;
    isSelected: boolean;
    isDetailTile: boolean;
    tileDisplayFields?: ITileDisplayField[] | undefined;
    listHttpRoot?: string;
    onTileSelect: (viewItem: any) => void;
    onTileCheck: (viewItem: any, checked: boolean) => void;
    onTileContextMenu: (ev: any, item: any) => void;
    getTimeZoneFormattedDate: (dateString: string, format: string) => string;
    openExternalResourceUrl?: (externalResourceUrl: IExternalResourceUrl) => void
}

export const ViewTile: React.FC<TileProps> = ({ isSharePointList, viewItem, isSelected, isDetailTile, tileDisplayFields, listHttpRoot, onTileSelect, onTileCheck, onTileContextMenu, getTimeZoneFormattedDate, openExternalResourceUrl }) => {
    const languageStrings = React.useContext(LanguageContext);

    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const metadataTextColors = useSelector((state: RootState) => state.tenantInfo?.metadataTextColors);

    const isTileLocked: boolean = React.useMemo(() => viewItem && viewItem[rowLockIndicator] === true, [viewItem]);

    const tileClassname: string = React.useMemo(() => {
        const classes: string[] = [tileStyles];

        if (isSelected) {
            classes.push("is-selected");
        }

        if (isDetailTile) {
            classes.push("detailed");
        }

        if (isSharePointList) {
            classes.push("no-image");
        }

        return classes.join(" ");
    }, [isSelected, isDetailTile, isSharePointList]);

    const handleContextMenu = React.useCallback(
        (event: React.MouseEvent<HTMLDivElement>) => {
            event.preventDefault();
            onTileContextMenu(event, viewItem);
        },
        [viewItem, onTileCheck, onTileContextMenu],
    );

    const getDisplayFieldValue = React.useCallback((displayField: ITileDisplayField) => {
        if (!viewItem || !displayField) {
            return <>-</>;
        }

        const { name, type, format, isCustomFormat } = displayField;
        const fieldValue = viewItem[name];

        switch (type) {
            case "Date":
            case "DateTime":
                return <>{(fieldValue && (format === "DateOnly" ? getTimeZoneFormattedDate(fieldValue, "LL") : getTimeZoneFormattedDate(fieldValue, "LLL"))) || "-"}</>;

            case "User":
            case "UserMulti":
                return <PeoplePills items={fieldValue} />;

            case "TaxonomyFieldType":
                return <DisplayText text={fieldValue?.Label?.toString()} />;

            case "Lookup":
            case "LookupMulti":
            case "TaxonomyFieldTypeMulti":
                // Some base Lookup fields (e.g. ItemChildCount/FolderChildCount) come back as a
                // plain scalar rather than a lookup array; render it directly instead of "-".
                // Only render primitives — an object would stringify to "[object Object]".
                if (!Array.isArray(fieldValue)) {
                    return fieldValue != null && fieldValue !== "" && typeof fieldValue !== "object"
                        ? <DisplayText text={fieldValue.toString()} />
                        : <>-</>;
                }
                const lookupKey = type === "TaxonomyFieldTypeMulti" ? "Label" : "lookupValue";
                const multiValue = fieldValue.map(item => item[lookupKey]).filter(Boolean).join(", ");
                return <DisplayText text={multiValue} />;

            case "URL":
                if (!fieldValue) return <>-</>;
                return <span><Link onClick={(async (e) => {
                    e.preventDefault();
                    if (fieldValue.endsWith && fieldValue.endsWith(openInHostDocubird)) {
                        const url: string = fieldValue
                            .replace(`?${openInHostDocubird}`, "")
                            .replace(`&${openInHostDocubird}`, "")
                            .trim();
                        const externalResourceUrl: IExternalResourceUrl = {
                            url,
                            description: viewItem[`${name}.desc`] && viewItem[`${name}.desc`] !== fieldValue
                                ? viewItem[`${name}.desc`]
                                : tryGetUrl(url)?.hostname ?? languageStrings.ExternalContent
                        }
                        openExternalResourceUrl(externalResourceUrl);
                    }
                    else {
                        const url = await getGridItemUrl(viewItem, fieldValue, listHttpRoot);
                        window.open(url, "_blank");
                    }
                })}>{viewItem[`${name}.desc`] || viewItem[name]}</Link></span>;

            case "Thumbnail":
                if (!fieldValue) return <>-</>;
                if (fieldValue?.thumbnailRenderer && fieldValue.thumbnailRenderer.spItemUrl) {
                    return <ImageRenderer imageName={fieldValue.fileName} imageSize="medium" imageItemUrl={fieldValue.thumbnailRenderer.spItemUrl} />
                }
                else if (fieldValue?.fileName) {
                    return <ImageRenderer imageName={fieldValue.originalImageName} imageSize="medium" imageItemUrl={`${currentDirectory.webUrl}/_api/v2.1/sites('${currentDirectory.siteId}')/lists('${currentDirectory.sharePointListId}')/items('${viewItem.ID}')/attachments('${encodeURIComponent(fieldValue.fileName)}')`} />
                }
                return <>-</>; 

            default:
                if (name === sharePointInternalFileName || (isSharePointList && name === sharePointInternalTitleName)) {
                    return <DisplayText text={fieldValue?.toString()} bold />;
                }

                if (name === "FileSizeDisplay") {
                    if (viewItem.FSObjType === "1") {
                        const folderCount = +(viewItem.FolderChildCount || 0);
                        const itemCount = +(viewItem.ItemChildCount || 0);
                        return <>{`${folderCount + itemCount} items`}</>;
                    }
                    const sizeInBytes = +(viewItem.File_x0020_Size || 0);
                    return <>{recentFilesAdvancedSearchMapper.convertBytes(sizeInBytes) || "-"}</>;
                }

                if (Array.isArray(fieldValue)) {
                    return <DisplayText text={fieldValue.filter(Boolean).join(", ")} />;
                }

                if (isCustomFormat && type !== "Note") {
                    return <ColouredDisplayText displayField={displayField} text={fieldValue?.toString()} metadataTextColors={metadataTextColors} />;
                }

                return <DisplayText text={fieldValue?.toString()} />;
        }
    }, [isSharePointList, viewItem, currentDirectory, metadataTextColors, getTimeZoneFormattedDate, openExternalResourceUrl]);

    const renderDisplayFields = React.useCallback(
        () => {
            return isDetailTile
                ? tileDisplayFields.map((displayField, index) => {
                    return <React.Fragment key={`${viewItem?.UniqueId}_${index}`}>
                        <Text style={textStyles} variant="small" styles={textTitleStyles}>{displayField.title}</Text>
                        <Text style={textStylesAlt} variant="medium">
                            {getDisplayFieldValue(displayField)}
                        </Text>
                    </React.Fragment>;
                })
                : <>
                    <Text title={viewItem?.FileLeafRef?.length > 38 ? viewItem?.FileLeafRef : undefined} style={textStylesNoSidePad} variant="medium">{viewItem?.FileLeafRef}</Text>
                    {(viewItem?.Created || viewItem?.Modified) && <Text style={textStyles} variant="small" styles={textTitleStyles}>{getTimeZoneFormattedDate(viewItem?.Created ?? viewItem?.Modified, "LL")}</Text>}
                </>;
        },
        [viewItem, tileDisplayFields, isDetailTile, getDisplayFieldValue],
    );

    const onTileClick = React.useCallback(() => {
        if (viewItem?.File_x0020_Type === "url") {
            onUrlClick(viewItem);
        }
        else {
            onTileSelect(viewItem);
        }
    }, [viewItem, onUrlClick, onTileSelect]);

    const onTileCheckClick = React.useCallback((ev) => {
        ev.stopPropagation();
        ev.preventDefault();
        onTileCheck(viewItem, !isSelected);
    }, [viewItem, isSelected, onTileCheck]);

    return (
        <div
            className={tileClassname}
            style={isTileLocked ? disabledTileStyles : undefined}
            onClick={onTileClick}
            onContextMenu={handleContextMenu}
        >
            <div className={tileCheckStyles} onClick={onTileCheckClick}>
                <Check checked={isSelected} />
            </div>
            {!isSharePointList && <ViewTileImageRenderer viewItem={viewItem} isDetailTile={isDetailTile} imageClassname={imageStyles} />}
            {renderDisplayFields()}
        </div>
    );
};